/**
 * 复制文本到剪贴板
 * @param {string} text
 * @returns {Promise<boolean>} true=成功 false=失败
 */
export async function copyText(text) {
  const value = String(text ?? '');
  // 现代浏览器（需 https 或 localhost）
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (e) {
      // 继续走兜底方案
    }
  }
  // 兜底：execCommand
  try {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (e) {
    return false;
  }
}

/**
 * 防抖函数
 * @param {Function} fn - 要防抖的函数
 * @param {number} delay - 延迟毫秒数
 * @returns {Function} 防抖后的函数
 */
export function debounce(fn, delay = 300) {
  let timer = null;
  return function (...args) {
    // 每次触发先清除定时器
    if (timer) clearTimeout(timer);
    // 重新计时
    timer = setTimeout(() => {
      fn.apply(this, args);
      timer = null;
    }, delay);
  };
}

/**
 * 对象转查询字符串（带问号）
 * @param {Record<string, unknown>} obj - 输入对象
 * @returns {string} 序列化后的查询字符串（如 ?key=value）
 */
export function objectToQueryString(obj) {
  // 1. 过滤无效值（null/undefined）
  const params = Object.entries(obj).filter(([, value]) => value !== null && value !== undefined);

  // 2. 无有效参数时返回空
  if (params.length === 0) return '';

  // 3. 编码处理（支持特殊字符）
  const encodedParams = params.map(([key, value]) => {
    // 处理数组/对象（可选：转为JSON字符串）
    const val = Array.isArray(value) || typeof value === 'object' ? JSON.stringify(value) : String(value);

    return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
  });

  // 4. 拼接结果
  return `?${encodedParams.join('&')}`;
}

/**
 * 对象转 GET URL 参数
 * @param {Object} params - 如 { pageNum: 1, pageSize: 10 }
 * @returns {string} 如 "pageNum=1&pageSize=10"
 */
export function objToUrlParams(params) {
  if (!params || typeof params !== 'object') return '';

  return Object.entries(params)
    .filter(([_, value]) => {
      // 过滤掉 null / undefined / 空字符串
      return value != null && value !== '';
    })
    .map(([key, value]) => {
      // 自动编码，防止特殊字符出错
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');
}
/**
 * 按环境打印日志
 * @param message 日志文案
 * @param data 可选附加数据
 * @param env 默认 'dev'（仅开发环境）；传 'all' 则所有环境都打印
 */
export type ConsoleLogEnv = 'dev' | 'test' | 'pre' | 'product' | 'all';

export function consoleLog(
  message: unknown,
  data?: unknown,
  env: ConsoleLogEnv = 'dev',
) {
  const currentEnv = String(import.meta.env.VITE_MODE || 'dev').replace(/['"]/g, '');
  const shouldLog = env === 'all' || env === currentEnv;
  if (!shouldLog) return;

  if (data !== undefined) {
    console.log(message, data);
  } else {
    console.log(message);
  }
}
